import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../../firebase';
import { collection, addDoc, getDocs, doc, getDoc, serverTimestamp } from 'firebase/firestore';
import { useAuth } from '../../context/AuthContext';
import Layout from '../../components/ui/Layout';
import { logAudit, AUDIT_ACTIONS } from '../../utils/audit';
import { IconArrowLeft, IconCheck } from '@tabler/icons-react';

export default function StaffInvite() {
  const navigate = useNavigate();
  const { orgId, userProfile, org } = useAuth();
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('operator');
  const [branchId, setBranchId] = useState(userProfile?.branchId || '');
  const [departmentId, setDepartmentId] = useState('');
  const [branches, setBranches] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [saving, setSaving] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  const resolvedOrgId = orgId || userProfile?.orgId;
  const isBranchManager = userProfile?.role === 'manager';
  const isSuperAdmin = userProfile?.role === 'super_admin';

  useEffect(() => {
    const load = async () => {
      if (!resolvedOrgId) return;
      try {
        const snap = await getDocs(collection(db, 'organisations', resolvedOrgId, 'branches'));
        setBranches(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      } catch (err) {
        console.error(err);
      }
    };
    load();
  }, [resolvedOrgId]);

  useEffect(() => {
    const loadDepts = async () => {
      if (!resolvedOrgId || !branchId) {
        setDepartments([]);
        return;
      }
      try {
        const snap = await getDoc(doc(db, 'organisations', resolvedOrgId, 'branches', branchId));
        if (snap.exists()) {
          setDepartments(snap.data().departments || []);
        }
      } catch (err) {
        console.error(err);
      }
    };
    loadDepts();
  }, [branchId, resolvedOrgId]);

  useEffect(() => {
    if (isBranchManager && userProfile?.branchId) {
      setBranchId(userProfile.branchId);
    }
  }, [isBranchManager, userProfile]);

  const handleInvite = async () => {
    if (!firstName || !lastName || !email) {
      setError('Please fill in all required fields');
      return;
    }
    if ((role === 'operator' || role === 'driver') && !branchId) {
      setError('Please select a branch');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const selectedBranch = branches.find(b => b.id === branchId);
      const fullName = `${firstName} ${lastName}`.trim();

      const inviteRef = await addDoc(collection(db, 'organisations', resolvedOrgId, 'invites'), {
        email,
        firstName,
        lastName,
        role,
        orgId: resolvedOrgId,
        orgName: org?.name || '',
        slug: org?.slug || '',
        brandColour: org?.brandColour || '#E8610A',
        branchId: branchId || null,
        branchName: selectedBranch?.name || null,
        departmentId: departmentId || null,
        level: role === 'operator' || role === 'driver' || role === 'manager' ? 'branch' : 'organisation',
        invitedByName: userProfile?.name || 'Your manager',
        invitedById: userProfile?.id || null,
        accepted: false,
        createdAt: serverTimestamp(),
        expiresAt: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000),
      });

      await logAudit(resolvedOrgId, {
        action: AUDIT_ACTIONS.STAFF_INVITED,
        performedBy: userProfile?.id,
        performedByName: userProfile?.name,
        targetId: inviteRef.id,
        targetType: 'user',
        targetName: fullName,
        details: `${fullName} invited as ${role}${selectedBranch ? ` — ${selectedBranch.name}` : ''}${departmentId ? ` · ${departmentId}` : ''}`,
        branchId: branchId || null,
        meta: { email, role },
      });

      setSent(true);
    } catch (err) {
      console.error(err);
      setError('Failed to send invitation — please try again');
    }
    setSaving(false);
  };

  const roleOptions = isBranchManager
    ? [
        { value: 'operator', label: 'Operator', desc: 'Carries out checks on assigned and pool assets' },
        { value: 'driver', label: 'Driver', desc: 'Vehicle and trailer checks only' },
      ]
    : [
        ...(isSuperAdmin
          ? [{ value: 'senior_manager', label: 'Senior manager', desc: 'All branches, manage assets, promote staff' }]
          : []),
        { value: 'manager', label: 'Branch manager', desc: 'Branch-scoped checks, incidents and staff' },
        { value: 'fuel_manager', label: 'Fuel manager', desc: 'Group-wide Derv management' },
        { value: 'operator', label: 'Operator', desc: 'Carries out checks on assigned and pool assets' },
        { value: 'driver', label: 'Driver', desc: 'Vehicle and trailer checks only' },
      ];

  const needsBranch = role === 'operator' || role === 'driver' || role === 'manager';
  const needsDepartment = role === 'operator' || role === 'driver';

  if (sent) return (
    <Layout>
      <div style={{ padding: 'var(--main-padding)', display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh' }}>
        <div style={{ textAlign: 'center', maxWidth: 400 }}>
          <div style={{ width: 64, height: 64, borderRadius: '50%', background: 'var(--color-success-light)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px' }}>
            <IconCheck size={28} color="var(--color-success)" stroke={1.5} />
          </div>
          <div style={{ fontSize: 20, fontWeight: 400, marginBottom: 8 }}>Invitation sent</div>
          <div style={{ fontSize: 13, color: 'var(--color-text-secondary)', lineHeight: 1.6, marginBottom: 24 }}>
            An invitation has been sent to <strong>{email}</strong>. They'll receive an email with a link to set up their account.
          </div>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
            <button
              onClick={() => { setSent(false); setFirstName(''); setLastName(''); setEmail(''); setRole('operator'); setDepartmentId(''); }}
              style={{ background: 'transparent', color: 'var(--color-text-secondary)', border: '0.5px solid var(--color-border-medium)', borderRadius: 'var(--radius-md)', padding: '10px 20px', fontSize: 13, cursor: 'pointer' }}
            >
              Invite another
            </button>
            <button
              onClick={() => navigate('/staff')}
              style={{ background: 'var(--color-brand)', color: '#fff', border: 'none', borderRadius: 'var(--radius-md)', padding: '10px 20px', fontSize: 13, cursor: 'pointer' }}
            >
              Back to staff
            </button>
          </div>
        </div>
      </div>
    </Layout>
  );

  return (
    <Layout>
      <div style={{ padding: 'var(--main-padding)' }}>
        <div onClick={() => navigate('/staff')} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 13, color: 'var(--color-text-secondary)', cursor: 'pointer', marginBottom: 20 }}>
          <IconArrowLeft size={15} stroke={1.5} /> Staff
        </div>

        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 28 }}>
          <div>
            <h1 style={{ fontSize: 22, fontWeight: 400, marginBottom: 4 }}>Invite staff member</h1>
            <p style={{ fontSize: 13, color: 'var(--color-text-tertiary)' }}>They'll receive an email invitation to set up their account.</p>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 28 }}>

          <div>
            <div style={{ fontSize: 14, fontWeight: 400, marginBottom: 16 }}>Personal details</div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
              <div>
                <label style={{ display: 'block', fontSize: 11, color: 'var(--color-text-secondary)', marginBottom: 6 }}>First name <span style={{ color: 'var(--color-danger)' }}>*</span></label>
                <input type="text" placeholder="Sarah" value={firstName} onChange={e => setFirstName(e.target.value)} style={{ width: '100%', boxSizing: 'border-box' }} />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 11, color: 'var(--color-text-secondary)', marginBottom: 6 }}>Last name <span style={{ color: 'var(--color-danger)' }}>*</span></label>
                <input type="text" placeholder="Mitchell" value={lastName} onChange={e => setLastName(e.target.value)} style={{ width: '100%', boxSizing: 'border-box' }} />
              </div>
            </div>

            <div style={{ marginBottom: 14 }}>
              <label style={{ display: 'block', fontSize: 11, color: 'var(--color-text-secondary)', marginBottom: 6 }}>Work email <span style={{ color: 'var(--color-danger)' }}>*</span></label>
              <input type="email" placeholder="sarah.mitchell@company.co.uk" value={email} onChange={e => setEmail(e.target.value)} style={{ width: '100%', boxSizing: 'border-box' }} />
            </div>

            {needsBranch && (
              <div style={{ marginBottom: 14 }}>
                <label style={{ display: 'block', fontSize: 11, color: 'var(--color-text-secondary)', marginBottom: 6 }}>Branch</label>
                {isBranchManager ? (
                  <div style={{ background: 'var(--color-bg-secondary)', border: '0.5px solid var(--color-border-light)', borderRadius: 'var(--radius-md)', padding: '8px 12px', fontSize: 13, color: 'var(--color-text-secondary)' }}>
                    {branches.find(b => b.id === branchId)?.name || branchId} — locked to your branch
                  </div>
                ) : (
                  <select value={branchId} onChange={e => { setBranchId(e.target.value); setDepartmentId(''); }} style={{ width: '100%', boxSizing: 'border-box' }}>
                    <option value="">Select branch</option>
                    {branches.map(b => (
                      <option key={b.id} value={b.id}>{b.name}</option>
                    ))}
                  </select>
                )}
              </div>
            )}

            {needsDepartment && branchId && departments.length > 0 && (
              <div style={{ marginBottom: 14 }}>
                <label style={{ display: 'block', fontSize: 11, color: 'var(--color-text-secondary)', marginBottom: 8 }}>Department</label>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {departments.map(d => (
                    <div
                      key={d}
                      onClick={() => setDepartmentId(d)}
                      style={{ flex: 1, minWidth: 100, padding: '10px 14px', borderRadius: 'var(--radius-md)', border: `0.5px solid ${departmentId === d ? 'var(--color-brand)' : 'var(--color-border-medium)'}`, background: departmentId === d ? 'var(--color-brand-light)' : 'transparent', color: departmentId === d ? 'var(--color-brand)' : 'var(--color-text-secondary)', fontSize: 13, cursor: 'pointer', textAlign: 'center', transition: 'all 0.15s', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}
                    >
                      {departmentId === d && <IconCheck size={13} stroke={2} />}
                      {d}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div>
            <div style={{ fontSize: 14, fontWeight: 400, marginBottom: 16 }}>Role</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {roleOptions.map(r => (
                <div
                  key={r.value}
                  onClick={() => { setRole(r.value); setDepartmentId(''); }}
                  style={{ padding: '12px 16px', borderRadius: 'var(--radius-lg)', border: `0.5px solid ${role === r.value ? 'var(--color-brand)' : 'var(--color-border-light)'}`, background: role === r.value ? 'var(--color-brand-light)' : 'var(--color-bg-primary)', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 12, transition: 'all 0.15s' }}
                >
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, color: role === r.value ? 'var(--color-brand)' : 'var(--color-text-primary)', marginBottom: 2 }}>{r.label}</div>
                    <div style={{ fontSize: 11, color: 'var(--color-text-tertiary)' }}>{r.desc}</div>
                  </div>
                  {role === r.value && (
                    <div style={{ width: 20, height: 20, borderRadius: '50%', background: 'var(--color-brand)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <IconCheck size={12} color="#fff" stroke={2.5} />
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        {error && (
          <div style={{ fontSize: 12, color: 'var(--color-danger)', background: 'var(--color-danger-light)', borderRadius: 'var(--radius-md)', padding: '10px 14px', marginTop: 20 }}>
            {error}
          </div>
        )}

        <div style={{ display: 'flex', gap: 10, marginTop: 24 }}>
          <button
            onClick={() => navigate('/staff')}
            style={{ background: 'transparent', color: 'var(--color-text-secondary)', border: '0.5px solid var(--color-border-medium)', borderRadius: 'var(--radius-md)', padding: '10px 24px', fontSize: 13, cursor: 'pointer' }}
          >
            Cancel
          </button>
          <button
            onClick={handleInvite}
            disabled={saving || !firstName || !lastName || !email}
            style={{ background: 'var(--color-brand)', color: '#fff', border: 'none', borderRadius: 'var(--radius-md)', padding: '10px 32px', fontSize: 13, cursor: saving || !firstName || !lastName || !email ? 'not-allowed' : 'pointer', opacity: saving || !firstName || !lastName || !email ? 0.5 : 1 }}
          >
            {saving ? 'Sending...' : 'Send invitation'}
          </button>
        </div>
      </div>
    </Layout>
  );
}
